import { useRef } from 'react';
import { useAlert } from 'react-alert';
import I18n from 'i18n-js';

import { setRecruiter } from '@lootboy/lib/actions/UserActions';
import {
  ERecruiterField,
  useRecruiterForm,
} from '@lootboy/lib/forms/useRecruiterForm';

import Alert from '~/components/Alert';
import Input from '~/components/atoms/FormControls/Input';
import { ScrollContainer } from '~/components/atoms/ScrollContainer';
import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';
import Image from '~/components/Image';
import BottomSheetDialog, {
  TBottomSheetDialogElement,
} from '~/components/molecules/BottomSheetDialog';

import { MENU_DIALOG_CONTAINER_ID } from '~/config';
import { useAppDispatch } from '~/redux/store/useAppDispatch';
import { parseLootBoyError } from '~/utilities/errors';

import heroImage from './recruiter-hero.png';

import styles from './RecruiterPopup.module.scss';

export function RecruiterPopup({ close }: { close: () => void }): JSX.Element {
  const alert = useAlert();
  const dialogElement = useRef<TBottomSheetDialogElement>(null);
  const dispatch = useAppDispatch();

  const {
    register,
    isSubmitEnabled,
    onFormSubmit,
    formState: { errors },
  } = useRecruiterForm(async (values) => {
    try {
      await dispatch(setRecruiter(values[ERecruiterField.USERNAME]));
      dialogElement.current?.close();
    } catch (error) {
      const { message } = parseLootBoyError(error);
      alert.error(
        <Alert
          title={I18n.t('common_error')}
          body={message}
        />,
      );
    }
  });

  return (
    <BottomSheetDialog
      ref={dialogElement}
      className={styles.dialog}
      elementIdToAnimate={MENU_DIALOG_CONTAINER_ID}
      onClose={close}
      header={I18n.t('MenuView_invite_bc')}
      contentClassName={styles.content}
    >
      <ScrollContainer>
        <div className={styles.container}>
          <div className={styles.heroContainer}>
            <Image
              alt="An envelope filled with lootcoins"
              src={heroImage}
              loading="eager"
              height={418}
              width={418}
              className={styles.hero}
            />
          </div>
          <h2 className={styles.headline}>
            {I18n.t('MenuView_invite_headline')}
          </h2>
          <form
            onSubmit={onFormSubmit}
            className={styles.form}
          >
            <Input
              {...register(ERecruiterField.USERNAME)}
              id={ERecruiterField.USERNAME}
              error={errors[ERecruiterField.USERNAME]?.message}
              label=""
              placeholder={I18n.t('MenuView_invite_placeholder')}
            />
            <StyledButton
              variant={EStyledButtonVariant.BLUE}
              type="submit"
              disabled={!isSubmitEnabled}
              className={styles.submitButton}
            >
              {I18n.t('confirm')}
            </StyledButton>
          </form>
        </div>
      </ScrollContainer>
    </BottomSheetDialog>
  );
}
